import React, { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Drawer, DrawerClose, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ELocalStorageKeys } from '@/lib/enums';
import { webStorageUtil } from '@/state/webStorage.state.service';

import { useFormik } from 'formik';
import { X } from 'lucide-react';

const DevBar = () => {
  const [showDevBar, setShowDevBar] = useState(false);
  const storeInputRef = useRef<HTMLInputElement>(null);

  const form = useFormik({
    initialValues: {
      mock_env: webStorageUtil.get(ELocalStorageKeys.MOCK_ENV) || 'dev',
      mock_override_store: webStorageUtil.get(ELocalStorageKeys.OVERRIDE_STORE) || '',
      mock_offers_api: webStorageUtil.get(ELocalStorageKeys.MOCK_OFFERS_API) || '',
      mock_boot_api: webStorageUtil.get(ELocalStorageKeys.MOCK_BOOT_API) || ''
    },
    onSubmit: (values) => {
      if (values.mock_env && values.mock_override_store) {
        webStorageUtil.set(ELocalStorageKeys.MOCK_ENV, values.mock_env);
        webStorageUtil.set(ELocalStorageKeys.OVERRIDE_STORE, values.mock_override_store);
        webStorageUtil.set(ELocalStorageKeys.MOCK_OFFERS_API, values.mock_offers_api);
        webStorageUtil.set(ELocalStorageKeys.MOCK_BOOT_API, values.mock_boot_api);
        setShowDevBar(false);
      }
    }
  });

  useEffect(() => {
    const konamiCode = ['ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight'];
    let currentCodeIndex = 0;

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === konamiCode[currentCodeIndex]) {
        currentCodeIndex++;
        // If the whole code is entered, show the dev bar
        if (currentCodeIndex === konamiCode.length) {
          setShowDevBar(true);
          currentCodeIndex = 0; // Reset the sequence
        }
      } else {
        // If the sequence is broken, reset
        currentCodeIndex = 0;
      }
    };

    // Add event listener for keydown
    document.addEventListener('keydown', handleKeydown);

    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  useEffect(() => {
    if (showDevBar) {
      console.log('DevBar opened, ref:', storeInputRef.current);
      setTimeout(() => {
        console.log('Attempting to focus, ref:', storeInputRef.current);
        storeInputRef.current?.focus();
      }, 0);
    }
  }, [showDevBar]);

  return (
    <div>
      {showDevBar && (
        <Drawer open={showDevBar} onOpenChange={setShowDevBar} direction='left'>
          <DrawerContent className='fixed left-0 top-[-100px] h-screen w-[400px] border-0 bg-black'>
            <div className='flex items-center justify-between p-8 pb-0'>
              <DrawerTitle className='text-lg font-bold text-green-500'>Appcharge Developer Tools</DrawerTitle>
              <DrawerClose className='p-1 hover:bg-accent'>
                <X />
              </DrawerClose>
            </div>
            <div className='p-8 pt-0'>
              <form onSubmit={form.handleSubmit} className='mx-auto max-w-3xl space-y-8 py-10 font-mono outline-none'>
                <div className='space-y-2'>
                  <Label className='text-green-500'>Environment</Label>
                  <Select onValueChange={(value) => form.setFieldValue('mock_env', value)} value={form.values.mock_env}>
                    <SelectTrigger className='font-mono'>
                      <SelectValue placeholder='Dev' />
                    </SelectTrigger>
                    <SelectContent className='bg-black font-mono text-white'>
                      <SelectItem value='dev' className='cursor-pointer'>
                        Dev
                      </SelectItem>
                      <SelectItem value='staging' className='cursor-pointer'>
                        Staging
                      </SelectItem>
                      <SelectItem value='sandbox' className='cursor-pointer'>
                        Sandbox
                      </SelectItem>
                      <SelectItem value='prod' className='cursor-pointer'>
                        Prod
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className='font-mono text-sm text-muted-foreground'>The API environment</p>
                  {form.errors.mock_env && (
                    <p className='font-mono text-sm text-destructive'>{String(form.errors.mock_env)}</p>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label className='text-green-500'>Override Store</Label>
                  <Input
                    ref={storeInputRef}
                    placeholder='For example: nir-dev.appcharge.com'
                    className='font-mono placeholder:text-gray-600'
                    {...form.getFieldProps('mock_override_store')}
                  />
                  <p className='font-mono text-sm text-muted-foreground'>Overrides the store domain</p>
                  {form.errors.mock_override_store && (
                    <p className='font-mono text-sm text-destructive'>{String(form.errors.mock_override_store)}</p>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label className='text-green-500'>Override Offers API</Label>
                  <Textarea
                    placeholder=''
                    className='resize-none font-mono'
                    {...form.getFieldProps('mock_offers_api')}
                  />
                  <p className='font-mono text-sm text-muted-foreground'>Overrides the offers API</p>
                  {form.errors.mock_offers_api && (
                    <p className='font-mono text-sm text-destructive'>{String(form.errors.mock_offers_api)}</p>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label className='text-green-500'>Override Boot API</Label>
                  <Textarea placeholder='' className='resize-none font-mono' {...form.getFieldProps('mock_boot_api')} />
                  <p className='font-mono text-sm text-muted-foreground'>Overrides the boot api</p>
                  {form.errors.mock_boot_api && (
                    <p className='font-mono text-sm text-destructive'>{String(form.errors.mock_boot_api)}</p>
                  )}
                </div>

                <div className='flex w-full flex-row gap-2'>
                  <Button type='submit' className='w-full bg-green-500 font-mono text-black hover:bg-green-600'>
                    Submit
                  </Button>
                  <Button
                    type='button'
                    className='w-full border border-white bg-black font-mono text-white hover:bg-black'>
                    Clear All Mocks
                  </Button>
                </div>
              </form>
            </div>
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
};

export default DevBar;
